import {useMemo} from 'react'
import {useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useI18n} from '@/i18n'
import {Parties, type PromotedListTheme} from '@/pages/Parties'
import {usePromotedTypes, findPromoted, pickPromotedLabel, PROMOTABLE_ENTITY_TABLES} from '@/hooks/use-promoted-types'
import {useAppConfig, type LocalizedTypeValue} from '@/hooks/use-app-config'
import {pickTypeValueLabel} from '@/lib/promoted-types-label'
import type {ConceptKey} from '@/lib/concepts'
import type {PromotedTypeView} from '@/types/types'

export function PartiesByType() {
    const {typeCode} = useParams<{typeCode: string}>()
    const {promoted} = usePromotedTypes()
    const {partyTypes} = useAppConfig()
    const {i18n: i18nInstance} = useTranslation()
    const i18n = useI18n()
    const lang = i18nInstance.language
    const createPrefix = i18n.common.create

    const code = (typeCode ?? '').toUpperCase()
    const theme = useMemo<PromotedListTheme | undefined>(
        () => buildTheme(promoted, partyTypes, code, lang, i18n.parties.allParties, createPrefix),
        [promoted, partyTypes, code, lang, i18n.parties.allParties, createPrefix],
    )

    return <Parties lockedType={code} themedAs={theme}/>
}

function buildTheme(
    promoted: PromotedTypeView[],
    partyTypes: LocalizedTypeValue[],
    code: string,
    lang: string,
    fallbackTitle: string,
    createPrefix: string,
): PromotedListTheme | undefined {
    if (!code) return undefined
    const entry = findPromoted(promoted, PROMOTABLE_ENTITY_TABLES.PARTY_TYPES, code)
    if (!entry) {
        return {title: fallbackTitle}
    }
    const pluralLabel = pickPromotedLabel(entry, lang)
    const singularLabel = pickTypeValueLabel(partyTypes, code, lang) ?? pluralLabel
    return {
        title: pluralLabel,
        icon: entry.icon as ConceptKey | undefined,
        createLabel: `${createPrefix} ${singularLabel}`,
    }
}
